import React from 'react';
import {
  Zap, Plus, Minus, SlidersHorizontal,
  Loader2, AlertCircle, Info
} from 'lucide-react';

const FundsActionPanel = ({
  fundsForm,
  setFundsForm,
  processing,
  error,
  onSubmit,
  selectedUser,
  userBalance,
  formatCurrency,
}) => {
  const hasUser = !!selectedUser;
  const action = fundsForm.action;
  const userCurrency = userBalance?.currency || 'USD';

  const handleActionChange = (newAction) => {
    setFundsForm(prev => ({ ...prev, action: newAction }));
  };

  const getButtonClass = () => {
    if (action === 'add') return 'add';
    if (action === 'remove') return 'remove';
    return 'adjust';
  };

  const getButtonLabel = () => {
    if (action === 'add') return 'Add Funds';
    if (action === 'remove') return 'Remove Funds';
    if (fundsForm.amount && parseFloat(fundsForm.amount) >= 0) return 'Increase Balance';
    if (fundsForm.amount && parseFloat(fundsForm.amount) < 0) return 'Decrease Balance';
    return 'Adjust Balance';
  };

  const getButtonIcon = () => {
    if (action === 'add') return <Plus size={16} />;
    if (action === 'remove') return <Minus size={16} />;
    return <SlidersHorizontal size={16} />;
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <h3><Zap /> Fund Actions</h3>
        {selectedUser && userBalance && (
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#059669' }}>
            Balance: {formatCurrency(userBalance.balance || userBalance.currentBalance, userCurrency)}
          </span>
        )}
      </div>

      <div className="panel-body funds-action-form">
        {/* User field (auto-filled from selected user) */}
        <div className="form-field" style={{ marginBottom: '0.85rem' }}>
          <label>User</label>
          <input
            type="text"
            value={fundsForm.userId}
            onChange={(e) => setFundsForm(prev => ({ ...prev, userId: e.target.value }))}
            placeholder={hasUser ? selectedUser.id : 'Select a user from the left panel'}
            disabled={processing}
            readOnly={hasUser}
          />
        </div>

        {/* Action type selector (segmented control) */}
        <div className="form-field" style={{ marginBottom: '0.85rem' }}>
          <label>Action</label>
          <div className="action-type-group">
            <button
              type="button"
              className={`action-type-btn ${action === 'add' ? 'active' : ''}`}
              onClick={() => handleActionChange('add')}
              disabled={processing}
            >
              <Plus size={14} /> Add
            </button>
            <button
              type="button"
              className={`action-type-btn ${action === 'remove' ? 'active' : ''}`}
              onClick={() => handleActionChange('remove')}
              disabled={processing}
            >
              <Minus size={14} /> Remove
            </button>
            <button
              type="button"
              className={`action-type-btn ${action === 'adjust' ? 'active' : ''}`}
              onClick={() => handleActionChange('adjust')}
              disabled={processing}
            >
              <SlidersHorizontal size={14} /> Adjust
            </button>
          </div>
        </div>

        {/* Amount field */}
        <div className="form-field" style={{ marginBottom: '0.85rem' }}>
          <label>Amount ({userCurrency})</label>
          {action === 'adjust' ? (
            <div className="adjust-wrapper">
              <span className="adjust-sign">
                {fundsForm.amount && parseFloat(fundsForm.amount) >= 0 ? '+' : ''}
              </span>
              <input
                type="number"
                step="0.01"
                value={fundsForm.amount}
                onChange={(e) => setFundsForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                disabled={processing}
              />
            </div>
          ) : (
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={fundsForm.amount}
              onChange={(e) => setFundsForm(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              disabled={processing}
            />
          )}
        </div>

        {/* Adjust hint */}
        {action === 'adjust' && (
          <div className="info-hint">
            <Info size={14} />
            Positive (+) to increase · Negative (-) to decrease
          </div>
        )}

        {/* Description field */}
        <div className="form-field" style={{ marginBottom: '0.85rem' }}>
          <label>Description (optional)</label>
          <textarea
            value={fundsForm.description}
            onChange={(e) => setFundsForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Note for this transaction..."
            disabled={processing}
            rows={2}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="error-alert">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          className={`btn-submit ${getButtonClass()}`}
          disabled={processing || !fundsForm.userId || !fundsForm.amount}
          onClick={onSubmit}
        >
          {processing ? (
            <>
              <Loader2 size={16} className="spin-icon" />
              Processing...
            </>
          ) : (
            <>
              {getButtonIcon()}
              {getButtonLabel()}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FundsActionPanel;
